import { useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import {
    AlertTriangle, CalendarClock, Check, CheckCircle2, ChevronDown, ChevronUp,
    Mic, MicOff, Phone, PhoneOff, RotateCw, Target, Wifi
} from 'lucide-react';

import SideDrawer from '../../shared/SideDrawer';
import ClampedText from '../../shared/display/ClampedText';
import Chip from '../crm.chip';
import CrmTriageBriefingCard from '../opportunity/briefing/crm.triage.briefing.card';
import ScheduleModal from '../opportunity/schedule/crm.opportunity.schedule.modal';

import masks from '../../../providers/masks';

import useVoiceCallEngine, { voiceCallRegisterStatuses } from '../../../hooks/crm/useVoiceCallEngine';
import useOpportunityScheduleFacts from '../../../hooks/crm/useOpportunityScheduleFacts';

import { voiceCallStatuses, voiceCallOutcomeOptions } from '../../../domain/constant/crm/crm.voice.call.status.constant';
import { chipTones, getContactTypeLabel, getPipelineChip } from '../../../domain/constant/crm/crm.chip.constant';
import { DRAWER_CONTENT_DELAY_MS, triageBriefingSurfaces } from '../../../domain/constant/crm/crm.triage.briefing.constant';
import { scheduleTexts } from '../../../domain/constant/crm/opportunity/crm.opportunity.schedule.constant';
import crmOpportunityScheduleService from '../../../domain/services/crm/crm.opportunity.schedule.service';

const maxDescriptionLines = 6;

const stageTitles = {
    pre: 'Ligar para contato',
    incall: 'Em ligação',
    ended: 'Chamada encerrada'
};

const getInitials = (name = '') =>
    name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('') || '?';

const ContactAvatar = ({ name, sizeClassName = 'h-[46px] w-[46px] flex-none text-base' }) => (
    <div className={`flex items-center justify-center rounded-full bg-primaryTint font-semibold text-primaryDark ${sizeClassName}`}>
        {getInitials(name)}
    </div>
);

const SectionLabel = ({ children, htmlFor }) => {

    const className = 'mb-2 block text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-400';

    if (htmlFor)
        return <label htmlFor={htmlFor} className={className}>{children}</label>;

    return <p className={className}>{children}</p>;
};

const SummaryRow = ({ label, value, detail }) => (
    <div className="flex justify-between gap-3 border-b border-gray-100 py-[9px] text-[13px] last:border-b-0">
        <span className="text-gray-500">{label}</span>
        <span className="text-right font-semibold text-gray-900">
            {value}
            {!!detail && <span className="ml-1.5 text-[12px] font-normal text-gray-500">{detail}</span>}
        </span>
    </div>
);

const CheckRow = ({ icon, name, children }) => (
    <div className="flex items-center gap-[11px] border-b border-gray-100 px-[14px] py-3 text-[13.5px] last:border-b-0">
        <span className="flex-none text-gray-500">{icon}</span>
        <span className="flex-1 font-medium text-gray-900">{name}</span>
        {children}
    </div>
);

const CheckStatus = ({ children }) => (
    <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-emerald-600">
        <Check className="h-3.5 w-3.5" />
        {children}
    </span>
);

const CallDrawer = ({ contact = {}, opportunity = {}, activity = null, scheduleOrigin = null, onConcludeActivity, onScheduled, onExit }) => {

    const engine = useVoiceCallEngine({ contactId: contact.id, opportunityId: opportunity.id, customerId: contact.customerId });
    const { opportunity: scheduleOpportunity } = useOpportunityScheduleFacts(opportunity);

    const [isOpen, setIsOpen] = useState(true);
    const [phoneNumber, setPhoneNumber] = useState(masks.cellPhone(contact.phone || ''));
    const [outcome, setOutcome] = useState('');
    const [notes, setNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isDeviceSelectorOpen, setIsDeviceSelectorOpen] = useState(false);
    const [isPhoneEditable, setIsPhoneEditable] = useState(false);
    const [isScheduleOpen, setIsScheduleOpen] = useState(false);

    const { enqueueSnackbar } = useSnackbar();

    const exitFiredRef = useRef(false);
    const fireExit = () => {
        if (exitFiredRef.current)
            return;

        exitFiredRef.current = true;
        onExit();
    };

    const exit = () => {
        setIsOpen(false);
        window.setTimeout(fireExit, 400);
    };

    const stage = (() => {
        if (engine.isCallFinished || (engine.hasCallStarted && engine.callStatus === voiceCallStatuses.error.key))
            return 'ended';

        if (engine.callIsInProgress)
            return 'incall';

        return 'pre';
    })();

    const isCallRegisterMissing = engine.voiceCallRegisterStatus === voiceCallRegisterStatuses.notFound
        || (stage === 'ended'
            && !engine.voiceCallId
            && engine.voiceCallRegisterStatus !== voiceCallRegisterStatuses.searching);
    const isCallRegisterSearching = engine.voiceCallRegisterStatus === voiceCallRegisterStatuses.searching;
    const isConnectionReady = engine.isOnline && engine.isDeviceReady && !engine.tokenError;

    const objective = opportunity.objective || activity?.description;

    const handleStartCall = () => engine.startCall(phoneNumber);

    const handleSaveAndConclude = async () => {
        setIsSaving(true);

        try {
            await engine.saveVoiceCallResult({
                outcome,
                callerComment: notes.trim() || null,
                activityId: activity?.opportunityActivityId || activity?.id || null,
            });
        } catch (error) {
            enqueueSnackbar('Não foi possível salvar o resultado da ligação.', { variant: 'error' });
            setIsSaving(false);
            return;
        }

        setIsSaving(false);
        enqueueSnackbar(
            activity ? 'Ligação registrada — concluindo a atividade...' : 'Ligação registrada.',
            { variant: 'success' }
        );
        exit();

        if (activity && onConcludeActivity)
            onConcludeActivity({ callerComment: notes.trim() });
    };

    const activityId = activity?.opportunityActivityId || activity?.id || null;

    const canScheduleFromDrawer = crmOpportunityScheduleService.canScheduleFromDrawer(scheduleOpportunity, activity);

    const needsCallResultBeforeSchedule = stage === 'ended' && !isCallRegisterMissing;

    const handleOpenSchedule = async () => {

        if (needsCallResultBeforeSchedule) {
            setIsSaving(true);

            try {
                await engine.saveVoiceCallResult({ outcome, callerComment: notes.trim() || null, activityId });
            } catch (error) {
                enqueueSnackbar('Não foi possível salvar o resultado da ligação.', { variant: 'error' });
                return;
            } finally {
                setIsSaving(false);
            }
        }

        setIsScheduleOpen(true);
    };

    const handleScheduled = (result) => {
        setIsScheduleOpen(false);
        exit();
        onScheduled && onScheduled(result);
    };

    const handleConcludeWithoutRegister = () => {
        exit();

        if (activity && onConcludeActivity)
            onConcludeActivity({ callerComment: notes.trim() });
    };

    const getStageDismissHandler = () => {
        if (stage === 'incall')
            return null;

        return exit;
    };

    const getStageCloseButtonHandler = () => {
        if (stage === 'incall')
            return engine.hangup;

        return exit;
    };

    const renderChips = () => {
        const contactTypeLabel = getContactTypeLabel(contact);
        const pipelineChip = getPipelineChip(opportunity.pipelineName);

        const chips = [
            contactTypeLabel && { key: contactTypeLabel, label: contactTypeLabel, tone: chipTones.outline },
            pipelineChip && { key: pipelineChip.label, ...pipelineChip },
            opportunity.statusName && { key: opportunity.statusName, label: opportunity.statusName, tone: chipTones.neutral }
        ].filter(Boolean);

        if (!chips.length)
            return null;

        return (
            <div className="mb-[22px] flex flex-wrap gap-[7px]">
                {chips.map(chip => (
                    <Chip key={chip.key} tone={chip.tone}>{chip.label}</Chip>
                ))}
            </div>
        );
    };

    const renderPhone = () => {

        if (isPhoneEditable)
            return (
                <input
                    id="call-phone-number"
                    type="tel"
                    aria-label="Telefone"
                    autoFocus
                    className="form-input mt-1 w-full rounded-md border-gray-300 py-1 text-[13px] tabular-nums focus:border-primary focus:ring-primary"
                    placeholder="(11) 99999-9999"
                    value={phoneNumber}
                    onChange={(event) => setPhoneNumber(masks.cellPhone(event.target.value))}
                />
            );

        return (
            <div className="mt-0.5 flex items-center gap-1.5 text-[13px] tabular-nums text-gray-500">
                <Phone className="h-3.5 w-3.5 flex-none text-gray-400" />
                {phoneNumber || 'Sem telefone'}
                <span aria-hidden="true">·</span>
                <button
                    type="button"
                    className="font-medium text-primary hover:underline"
                    onClick={() => setIsPhoneEditable(true)}
                >
                    Editar
                </button>
            </div>
        );
    };

    const renderPreStage = () => (
        <div>

            <div className="mb-[13px] flex items-center gap-[14px]">
                <ContactAvatar name={contact.name} />

                <div className="min-w-0 flex-1">
                    <p className="truncate text-[17px] font-semibold text-gray-900">
                        {contact.name || 'Contato sem nome'}
                    </p>
                    {renderPhone()}
                </div>
            </div>

            {renderChips()}

            <CrmTriageBriefingCard
                briefing={scheduleOpportunity.triageBriefing}
                opportunityId={opportunity.id}
                surface={triageBriefingSurfaces.call}
                className="mb-4"
                enterDelayMs={DRAWER_CONTENT_DELAY_MS}
            />

            {(!!opportunity.customerFacts?.length || opportunity.lastInteraction || opportunity.abandonedCartFact) && (
                <>
                    <SectionLabel>Resumo da oportunidade</SectionLabel>
                    <div className="mb-4 rounded-lg border border-gray-200 px-[14px] py-1">
                        {opportunity.lastInteraction && <SummaryRow label="Última interação" value={opportunity.lastInteraction} />}
                        {(opportunity.customerFacts || []).map(({ key, ...fact }) => <SummaryRow key={key} {...fact} />)}
                        {opportunity.abandonedCartFact && <SummaryRow {...opportunity.abandonedCartFact} />}
                    </div>
                </>
            )}

            {opportunity.description && (
                <>
                    <SectionLabel>Descrição</SectionLabel>
                    <div className="mb-4 rounded-lg border border-gray-200 px-[14px] py-3">
                        <ClampedText
                            lines={maxDescriptionLines}
                            className="whitespace-pre-line text-[13.5px] leading-relaxed text-gray-700"
                        >
                            {opportunity.description}
                        </ClampedText>
                    </div>
                </>
            )}

            {objective && (
                <div className="mb-5 flex gap-[9px] rounded-lg bg-primaryTint px-[14px] py-3 text-[13.5px] leading-normal text-gray-800">
                    <Target className="mt-0.5 h-4 w-4 flex-none text-primaryDark" />
                    <span>{objective}</span>
                </div>
            )}

            {engine.tokenError && (
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-red-600" />
                    <div className="flex-1">
                        <p className="text-[13.5px] text-red-700">{engine.tokenError}</p>
                        <button
                            type="button"
                            className="mt-1 inline-flex items-center gap-1 text-[13px] font-medium text-red-700 underline"
                            onClick={engine.retrySetup}
                        >
                            <RotateCw className="h-3.5 w-3.5" />
                            Tentar novamente
                        </button>
                    </div>
                </div>
            )}

            {!engine.tokenError && engine.errorMessage && (
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-red-600" />
                    <p className="text-[13.5px] text-red-700">{engine.errorMessage}</p>
                </div>
            )}

            <SectionLabel>Verificação de conexão</SectionLabel>

            <div className="overflow-hidden rounded-lg border border-gray-200">

                <CheckRow icon={<Wifi className="h-[18px] w-[18px]" />} name="Conexão">
                    {isConnectionReady
                        ? <CheckStatus>Estável</CheckStatus>
                        : (
                            <span className="text-[12.5px] font-semibold text-amber-600">
                                {engine.isOnline ? 'Preparando...' : 'Offline'}
                            </span>
                        )}
                </CheckRow>

                <CheckRow icon={<Mic className="h-[18px] w-[18px]" />} name="Microfone">
                    {engine.isMicrophoneReady
                        ? <CheckStatus>Autorizado</CheckStatus>
                        : (
                            <button
                                type="button"
                                className="text-[12.5px] font-semibold text-primary hover:underline disabled:opacity-50"
                                onClick={engine.testMicrophone}
                                disabled={engine.isTestingMicrophone}
                            >
                                {engine.isTestingMicrophone
                                    ? 'Testando...'
                                    : engine.microphoneStatus === 'blocked' ? 'Bloqueado — testar novamente' : 'Testar microfone'}
                            </button>
                        )}
                </CheckRow>

            </div>

            <button
                type="button"
                className="mt-2.5 flex items-center gap-1 text-[12.5px] text-gray-500 hover:text-gray-700"
                onClick={() => setIsDeviceSelectorOpen(prevState => !prevState)}
            >
                {isDeviceSelectorOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                Dispositivo de entrada
            </button>

            {isDeviceSelectorOpen && (
                <select
                    aria-label="Microfone"
                    className="form-select mt-2 w-full rounded-md border-gray-300 text-[13px] focus:border-primary focus:ring-primary"
                    value={engine.selectedInputDeviceId}
                    onChange={(event) => engine.changeInputDevice(event.target.value)}
                >
                    {engine.audioInputDevices.length === 0 && (
                        <option value="">Nenhum microfone encontrado</option>
                    )}
                    {engine.audioInputDevices.map(inputDevice => (
                        <option key={inputDevice.deviceId} value={inputDevice.deviceId}>
                            {inputDevice.label || `Microfone ${inputDevice.deviceId}`}
                        </option>
                    ))}
                </select>
            )}

            {engine.canStartCall && phoneNumber && (
                <div className="mt-3.5 flex items-center gap-2 rounded-lg bg-emerald-50 px-[14px] py-[11px] text-[13px] font-medium text-emerald-700">
                    <Check className="h-4 w-4 flex-none" />
                    Tudo pronto para iniciar a ligação
                </div>
            )}

        </div>
    );

    const renderInCallStage = () => (
        <div className="flex flex-col items-center pt-9 text-center">

            <div className="relative mb-[18px]">
                <span className="absolute -inset-2 animate-ping rounded-full border-2 border-primary opacity-40" aria-hidden="true" />
                <ContactAvatar name={contact.name} sizeClassName="relative h-[88px] w-[88px] flex-none text-3xl" />
            </div>

            <p className="text-xl font-semibold text-gray-900">{contact.name || 'Contato sem nome'}</p>
            <p className="mt-1 flex items-center gap-1.5 text-[13px] tabular-nums text-gray-500">
                <Phone className="h-3.5 w-3.5 text-gray-400" />
                {phoneNumber}
            </p>

            <p className="mt-[18px] text-[13px] font-medium text-primary">
                {voiceCallStatuses[engine.callStatus]?.message || 'Chamando...'}
            </p>

            <p className="mt-1.5 text-[34px] font-semibold tabular-nums tracking-[0.02em] text-gray-900">
                {engine.duration}
            </p>

            <button
                type="button"
                className={`mt-[30px] flex w-[78px] flex-col items-center gap-1.5 rounded-[10px] border py-3 text-xs font-medium transition-colors disabled:opacity-50 ${
                    engine.isMuted
                        ? 'border-primary bg-primaryTint text-primaryDark'
                        : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                }`}
                onClick={engine.toggleMute}
                disabled={!engine.isInCall}
            >
                {engine.isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                Mudo
            </button>

        </div>
    );

    const renderEndedStage = () => (
        <div>

            <div className="mb-[22px]">
                <div className="flex items-center gap-[14px] rounded-[10px] bg-emerald-50 p-4">
                    <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-white text-emerald-600">
                        <CheckCircle2 className="h-[26px] w-[26px]" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[15px] font-semibold text-gray-900">{contact.name || 'Contato'}</p>
                        <p className="mt-0.5 text-[13px] text-gray-500">
                            Duração da chamada: <b className="font-semibold tabular-nums text-gray-900">{engine.duration}</b>
                        </p>
                    </div>
                </div>
                {engine.callEndReason && (
                    <p className="mt-2 px-1 text-[12.5px] text-gray-500">{engine.callEndReason}</p>
                )}
            </div>

            {isCallRegisterMissing && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
                    <p className="text-[13.5px] text-amber-800">
                        Não localizamos o registro desta chamada. O resultado não será salvo no histórico da
                        ligação{activity ? ', mas você ainda pode concluir a atividade' : ''}.
                    </p>
                </div>
            )}

            {!isCallRegisterMissing && (
                <>
                    <SectionLabel>Resultado da ligação</SectionLabel>
                    <div className="mb-5 flex flex-wrap gap-2" role="group" aria-label="Resultado da ligação">
                        {voiceCallOutcomeOptions.map(option => (
                            <button
                                key={option.key}
                                type="button"
                                className={`rounded-full border px-[13px] py-[7px] text-[12.5px] font-medium transition-colors ${
                                    outcome === option.key
                                        ? 'border-primary bg-primary text-white'
                                        : 'border-gray-300 text-gray-500 hover:border-primary hover:text-primaryDark'
                                }`}
                                onClick={() => setOutcome(option.key)}
                                disabled={isCallRegisterSearching}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    {isCallRegisterSearching && (
                        <p className="-mt-3 mb-5 text-xs text-gray-500">Localizando o registro da chamada...</p>
                    )}

                    <SectionLabel htmlFor="call-notes">Observações</SectionLabel>
                    <textarea
                        id="call-notes"
                        className="form-textarea min-h-[120px] w-full resize-y rounded-lg border-gray-200 px-[13px] py-[11px] text-[13.5px] leading-normal focus:border-primary focus:ring-primary"
                        placeholder="Anote o que foi conversado, objeções e próximos passos..."
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                    />
                </>
            )}

        </div>
    );

    const footerButtonClassName = 'flex h-[46px] w-full items-center justify-center gap-2 rounded text-[15px] font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50';

    const renderScheduleAction = () => {
        const isActionVisible = canScheduleFromDrawer && stage !== 'incall';

        if (!isActionVisible && !isScheduleOpen)
            return null;

        return (
            <>
                {isActionVisible && (
                    <button
                        type="button"
                        className="mb-2.5 flex h-[42px] w-full items-center justify-center gap-2 rounded border border-gray-300 text-[14px] font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={handleOpenSchedule}
                        disabled={isSaving || isCallRegisterSearching || (needsCallResultBeforeSchedule && !outcome)}
                    >
                        <CalendarClock className="h-4 w-4" />
                        {scheduleTexts.drawerActionLabel}
                    </button>
                )}

                <ScheduleModal
                    isOpen={isScheduleOpen}
                    opportunity={opportunity}
                    activityId={activityId}
                    origin={scheduleOrigin}
                    onClose={() => setIsScheduleOpen(false)}
                    onScheduled={handleScheduled}
                />
            </>
        );
    };

    const renderStageFooter = () => {
        if (stage === 'pre')
            return (
                <button
                    type="button"
                    className={`${footerButtonClassName} bg-primary hover:bg-primaryDark`}
                    onClick={handleStartCall}
                    disabled={!engine.canStartCall || !phoneNumber}
                >
                    <Phone className="h-[18px] w-[18px]" />
                    Iniciar ligação
                </button>
            );

        if (stage === 'incall')
            return (
                <button
                    type="button"
                    className={`${footerButtonClassName} bg-red-600 hover:bg-red-700`}
                    onClick={engine.hangup}
                >
                    <PhoneOff className="h-[18px] w-[18px]" />
                    Encerrar chamada
                </button>
            );

        if (isCallRegisterMissing)
            return (
                <button
                    type="button"
                    className={`${footerButtonClassName} bg-primary hover:bg-primaryDark`}
                    onClick={handleConcludeWithoutRegister}
                >
                    {activity ? 'Concluir atividade' : 'Fechar'}
                </button>
            );

        return (
            <button
                type="button"
                className={`${footerButtonClassName} bg-primary hover:bg-primaryDark`}
                onClick={handleSaveAndConclude}
                disabled={!outcome || isSaving || isCallRegisterSearching}
            >
                {isSaving ? 'Salvando...' : activity ? 'Salvar e concluir' : 'Salvar'}
            </button>
        );
    };

    const renderFooter = () => (
        <>
            {renderScheduleAction()}
            {renderStageFooter()}
        </>
    );

    const stageContentByKey = {
        pre: renderPreStage,
        incall: renderInCallStage,
        ended: renderEndedStage
    };

    return (
        <SideDrawer
            isOpen={isOpen}
            onDismiss={getStageDismissHandler()}
            onCloseButtonClick={getStageCloseButtonHandler()}
            afterLeave={fireExit}
            title={stageTitles[stage]}
            footer={renderFooter()}
        >
            {stageContentByKey[stage]()}
        </SideDrawer>
    );
};

export default CallDrawer;
